import type { AppRouter } from "@newform-monorepo/backend/src/router";
import { TRPCClientError, type TRPCLink } from "@trpc/client";
import { observable } from "@trpc/server/observable";

export function isSessionExpiredError(error: Error): boolean {
  return (
    error instanceof TRPCClientError &&
    error.data?.code === "UNAUTHORIZED" &&
    error.data.sessionExpired === true
  );
}

export function createSessionRecovery(getSession: () => string) {
  let failedSession: string | null = null;
  const listeners = new Set<() => void>();
  const publish = () => {
    for (const listener of listeners) listener();
  };
  return {
    getSnapshot: () => failedSession,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    report(session: string) {
      if (session !== getSession() || failedSession === session) return;
      failedSession = session;
      publish();
    },
    dismissIfSessionChanged() {
      if (failedSession !== null && failedSession !== getSession()) {
        failedSession = null;
        publish();
        return true;
      }
      return false;
    },
    async recover(returnTo: string) {
      if (failedSession === null || this.dismissIfSessionChanged()) {
        return "changed" as const;
      }
      const response = await fetch("/api/auth/recover-session", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ failedSession, returnTo }),
      });
      if (response.status === 409) {
        failedSession = null;
        publish();
        return "changed" as const;
      }
      if (!response.ok) throw new Error("Could not restart sign-in.");
      // A different tab may have signed in while the request was running.
      if (getSession() && getSession() !== failedSession) {
        failedSession = null;
        publish();
        return "changed" as const;
      }
      return "ready" as const;
    },
  };
}

export type SessionRecovery = ReturnType<typeof createSessionRecovery>;

export function sessionRecoveryLink(
  recovery: SessionRecovery,
  getSession: () => string,
): TRPCLink<AppRouter> {
  return () =>
    ({ op, next }) =>
      observable((observer) => {
        const trackedOp = {
          ...op,
          context: { ...op.context, sessionRecoveryToken: getSession() },
        };
        return next(trackedOp).subscribe({
          next(value) {
            observer.next(value);
          },
          error(error) {
            // Streamed batches can return HTTP 200 with a failed operation.
            // Only the auth middleware's marker means the login is invalid;
            // a password/login procedure can also return UNAUTHORIZED.
            if (
              error.data?.code === "UNAUTHORIZED" &&
              error.data.sessionExpired
            ) {
              recovery.report(trackedOp.context.sessionRecoveryToken);
            }
            observer.error(error);
          },
          complete() {
            observer.complete();
          },
        });
      });
}
